import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SupervisorMentor } from "@/lib/api/user";
import { useToast } from "@/components/ui/use-toast";
import { useUnassignSupervisorMentor } from "@/lib/hooks/mentors/use-unassign-supervisor-mentors";
import { useState, useEffect } from "react";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CheckCircle2Icon, XCircleIcon, Loader2Icon } from "lucide-react";
import { cn } from "@/lib/utils";

interface BatchOperationModalProps {
  isOpen: boolean;
  onClose: () => void;
  operation: "unassign" | "reassign" | null;
  selectedMentors: SupervisorMentor[];
  userId: string;
}

interface OperationStatus {
  mentorId: string;
  mentorName: string;
  status: "pending" | "success" | "error";
  error?: string;
}

export const BatchOperationModal = ({
  isOpen,
  onClose,
  operation,
  selectedMentors: initialSelectedMentors,
  userId,
}: BatchOperationModalProps) => {
  const { toast } = useToast();
  const { mutateAsync: unassignMentor } = useUnassignSupervisorMentor();
  const [operationStatuses, setOperationStatuses] = useState<OperationStatus[]>(
    [],
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedMentors, setSelectedMentors] = useState<SupervisorMentor[]>(
    [],
  );

  useEffect(() => {
    if (isOpen) {
      setSelectedMentors(initialSelectedMentors);
    }
  }, [isOpen, initialSelectedMentors]);

  const handleClose = () => {
    if (!isProcessing) {
      setOperationStatuses([]);
      setSelectedMentors([]);
      onClose();
    }
  };

  const removeMentor = (mentorId: string) => {
    setSelectedMentors((prev) => prev.filter((m) => m.mentor_id !== mentorId));
  };

  const processOperations = async () => {
    setIsProcessing(true);
    setOperationStatuses(
      selectedMentors.map((mentor) => ({
        mentorId: mentor.mentor_id,
        mentorName: mentor.mentor_name,
        status: "pending",
      })),
    );

    let successCount = 0;
    let failureCount = 0;

    for (const mentor of selectedMentors) {
      try {
        if (operation === "unassign") {
          await unassignMentor({
            mentorId: mentor.mentor_id,
            supervisorId: userId,
          });
          setOperationStatuses((prev) =>
            prev.map((status) =>
              status.mentorId === mentor.mentor_id
                ? { ...status, status: "success" }
                : status,
            ),
          );
          successCount++;
        }
        // Add reassign logic here when needed
      } catch (error) {
        setOperationStatuses((prev) =>
          prev.map((status) =>
            status.mentorId === mentor.mentor_id
              ? { ...status, status: "error", error: "Operation failed" }
              : status,
          ),
        );
        failureCount++;
      }
    }

    setIsProcessing(false);
    toast({
      title: "Batch Operation Complete",
      description: `Successfully ${operation}ed ${successCount} mentors. ${failureCount > 0 ? `Failed: ${failureCount}` : ""}`,
      variant: failureCount > 0 ? "destructive" : "default",
    });

    if (failureCount === 0) {
      handleClose();
    }
  };

  const progress =
    operationStatuses.length > 0
      ? (operationStatuses.filter((s) => s.status === "success").length /
          operationStatuses.length) *
        100
      : 0;

  const showActionButton =
    selectedMentors.length > 0 &&
    !isProcessing &&
    operationStatuses.length === 0;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {operation === "unassign" ? "Unassign" : "Reassign"} Mentors
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          <div className="mb-2 text-sm">Selected mentors to {operation}:</div>
          <ScrollArea className="h-[200px]">
            <div className="space-y-2 pr-4">
              {selectedMentors.map((mentor) => (
                <div
                  key={mentor.mentor_id}
                  className="flex items-center justify-between rounded bg-gray-50 p-2 text-sm"
                >
                  <span>{mentor.mentor_name}</span>
                  {!isProcessing && operationStatuses.length === 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => removeMentor(mentor.mentor_id)}
                    >
                      ×
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>

        {operationStatuses.length > 0 && (
          <div className="mb-4">
            <Progress value={progress} className="mb-4" />
            <ScrollArea className="h-[200px]">
              <div className="space-y-2 pr-4">
                {operationStatuses.map((status) => (
                  <div
                    key={status.mentorId}
                    className={cn(
                      "flex items-center justify-between rounded p-2",
                      status.status === "success" && "bg-green-50",
                      status.status === "error" && "bg-red-50",
                    )}
                  >
                    <span className="text-sm">{status.mentorName}</span>
                    <div className="flex items-center">
                      {status.status === "pending" && (
                        <Loader2Icon className="h-4 w-4 animate-spin text-gray-500" />
                      )}
                      {status.status === "success" && (
                        <CheckCircle2Icon className="h-4 w-4 text-green-500" />
                      )}
                      {status.status === "error" && (
                        <XCircleIcon className="h-4 w-4 text-red-500" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}

        <div className="flex justify-end space-x-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isProcessing}
          >
            {isProcessing ? "Please wait..." : "Cancel"}
          </Button>
          {showActionButton && (
            <Button
              variant="destructive"
              onClick={processOperations}
              disabled={selectedMentors.length === 0 || isProcessing}
            >
              {isProcessing && (
                <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
              )}
              {operation === "unassign" ? "Unassign" : "Reassign"}{" "}
              {selectedMentors.length > 0 ? `(${selectedMentors.length})` : ""}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
